import React from "react";
import DocsCallout from "../../../components/dashboard/common/Documentation/DocsCallout";
import DocsPageHeader from "../../../components/dashboard/common/Documentation/DocsPageHeader";
import SearchHighlighter from "../../../components/dashboard/common/Documentation/SearchHighlighter";

const CredentialVault = () => {
  return (
    <SearchHighlighter>
      <DocsPageHeader 
        number="4" 
        title="Credential Vault & Secure Integration" 
        description="The Credential Vault enables authenticated penetration testing by providing researchers with secure, scoped access tokens. The vault uses a zero-knowledge architecture — plaintext credentials never reach CTB servers."
      />


      <h2 id="vault-lifecycle" className="text-xl font-bold text-black mt-10 mb-4">4.1 Configuration & Access Lifecycle</h2>
      <p className="mb-6 text-slate-600">The Credential Vault follows a strict 8-step lifecycle from creation to secure researcher access.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        {[
          { step: 1, label: "Create Vault", desc: "Define a secure container for sensitive data." },
          { step: 2, label: "Set Master PW", desc: "Establish your private, zero-knowledge encryption key." },
          { step: 3, label: "Add Credentials", desc: "Store API keys, secrets, or SSH keys securely." },
          { step: 4, label: "Link to Program", desc: "Attach the vault to an active testing engagement." },
          { step: 5, label: "Set Access PW", desc: "Create a separate, shareable password for researchers." },
          { step: 6, label: "Share Password", desc: "Securely transmit the Access Password to your team." },
          { step: 7, label: "Researcher Auth", desc: "Researcher authenticates using the shared Access Password." },
          { step: 8, label: "Secure Reveal", desc: "Credentials decrypted and available for security testing." },
        ].map(item => (
          <div key={item.step} className="p-4 rounded-xl border border-[#eaeaea] bg-[#fafafa]/50">
            <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Step {item.step}</span>
            <h4 className="font-bold text-black text-xs mt-1">{item.label}</h4>
            <p className="text-[10px] text-slate-500 mt-1 leading-tight">{item.desc}</p>
          </div>
        ))}
      </div>

      <h2 id="security-concepts" className="text-xl font-bold text-black mt-12 mb-4">4.2 Critical Security Concepts</h2>
      <p className="text-slate-600 mb-6 leading-relaxed">Maintaining separate password boundaries is the cornerstone of the vault's security architecture.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        <div className="rounded-2xl border border-red-100 p-6 bg-red-50/30">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-red-100 rounded-lg text-red-600">🔒</div>
            <h3 className="font-bold text-red-900">Master Password</h3>
          </div>
          <ul className="text-xs space-y-2 text-red-800 list-none p-0">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-red-400 shrink-0" />
              <span><b>Private:</b> Your personal key to manage the vault.</span>
            </li>
            <li className="flex items-start gap-2 text-red-600 font-bold">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-red-600 shrink-0" />
              <span>NEVER share with researchers or CTB support.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-red-400 shrink-0" />
              <span>Used to encrypt/decrypt all stored secrets.</span>
            </li>
          </ul>
        </div>

        <div className="rounded-2xl border border-green-100 p-6 bg-green-50/30">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-100 rounded-lg text-green-600">🔑</div>
            <h3 className="font-bold text-green-900">Access Password</h3>
          </div>
          <ul className="text-xs space-y-2 text-green-800 list-none p-0">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-green-400 shrink-0" />
              <span><b>Shareable:</b> Created specifically for researchers.</span>
            </li>
            <li className="flex items-start gap-2 text-green-600 font-bold">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-green-600 shrink-0" />
              <span>Safe to share via secure messaging.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-green-400 shrink-0" />
              <span>Can be revoked or rotated at any time.</span>
            </li>
          </ul>
        </div>
      </div>

      <h2 id="encryption" className="text-xl font-bold text-black mt-12 mb-4">4.3 Technical Encryption Architecture</h2>
      <div className="overflow-x-auto border border-[#eaeaea] rounded-lg mt-6">
        <table className="min-w-full divide-y divide-[#eaeaea] m-0">
          <thead className="bg-[#fafafa]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-[#eaeaea]">Property</th>
              <th className="px-6 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">Specification</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#eaeaea]">
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Cipher</td>
              <td className="px-6 py-4 text-sm text-slate-600">AES-256-GCM — authenticated encryption with associated data</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Key Derivation</td>
              <td className="px-6 py-4 text-sm text-slate-600">PBKDF2 with 600,000 iterations — protects against offline brute-force</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Master Password</td>
              <td className="px-6 py-4 text-sm text-slate-600">User-owned. No recovery mechanism. Loss results in permanent vault inaccessibility.</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Decryption Key</td>
              <td className="px-6 py-4 text-sm text-slate-600">Ephemeral — lives in RAM only. Cleared automatically on logout.</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-black border-r border-[#eaeaea]">Server Access</td>
              <td className="px-6 py-4 text-sm text-slate-600">Zero. Plaintext credentials never transit CTB infrastructure.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 id="credential-types" className="text-xl font-bold text-black mt-12 mb-4">4.2 Supported Credential Types</h2>
      <ul className="mt-6 space-y-4 list-disc list-inside text-slate-600">
        <li><b className="text-black">HTTP Basic Auth</b> — Username and password pairs for HTTP-authenticated endpoints.</li>
        <li><b className="text-black">OAuth2 / JWT</b> — Bearer tokens and client credentials for API authentication flows.</li>
        <li><b className="text-black">SSH Private Key</b> — RSA or ED25519 keys for server-level access during infrastructure assessments.</li>
        <li><b className="text-black">mTLS / Client Certificates</b> — Certificate and private key pairs for mutual TLS endpoints.</li>
        <li><b className="text-black">Database Credentials</b> — Direct database connection strings for data-layer testing.</li>
        <li><b className="text-black">Mobile API App Credentials</b> — App-specific keys and secrets for mobile backend APIs.</li>
      </ul>

      <DocsCallout type="warning">
        <b>Escalation Testing Requirement:</b> For authenticated testing, provide separate accounts for each permission tier — for example, <code>standard_user</code>, <code>manager_role</code>, and <code>platform_admin</code>. This allows researchers to verify both lateral and vertical privilege escalation boundaries.
      </DocsCallout>

      <div className="mt-8 p-6 rounded-lg border border-[#eaeaea] bg-[#fafafa]">
        <span className="font-bold text-black">Atomic Mapping:</span> Each set of credentials is mapped only to specific program runs. Researchers receive only the access tokens relevant to their assigned testing scope.
      </div>

      <h2 id="faq" className="text-xl font-bold text-black mt-12 mb-4">4.5 FAQ & Troubleshooting</h2>
      <div className="space-y-4">
        <div className="p-4 rounded-xl border border-[#eaeaea] bg-white">
          <h4 className="font-bold text-black text-sm">What happens if I forget my Master Password?</h4>
          <p className="text-xs text-slate-500 mt-2 italic"><b>Loss of Access:</b> Due to our zero-knowledge architecture, CTB does not store your master password. If lost, your vault data is permanently unrecoverable. We recommend using an enterprise password manager to store it.</p>
        </div>
        <div className="p-4 rounded-xl border border-[#eaeaea] bg-white">
          <h4 className="font-bold text-black text-sm">Can I revoke researcher access mid-program?</h4>
          <p className="text-xs text-slate-500 mt-2 italic"><b>Instant Revocation:</b> Yes. You can unlink the vault from the program or rotate the Access Password in the Vault Management settings. All decrypted views by researchers are logged in your security audit trail.</p>
        </div>
      </div>
    </SearchHighlighter>
  );
};

export default CredentialVault;
